import React, {useEffect, useRef, useState} from 'react';
import './NewAlternateView.css';
import CameraSidePanelView from "./CameraSidePanelView";


import {
    ApiBankInfo,
    ApiBuildingInfo,
    ApiDeviceInfo,
    ApiFaultLog,
    ApiMaintenanceLogItem,
    GeneralOperatingData,
    getBuildingInfos,
    getFaultLogs,
    getGeneralOperatingDataForDevices,
    getMaintenanceLogs,
    getSolutrakControllerTemperatureDataPoint,
    getSolutrakDoorCycleCounts,
    getSolutrakDoorCycleDataPoints,
    getSolutrakLast24HoursCarCallCountDataPoints,
    getSolutrakLoadLevelDataPoints,
    getSolutrakRunCounts,
    getSolutrakUpDownTimeDataPoints,
} from './solutrakApiService';
import {PerformanceData} from '../../../types/performance';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import {
    Activity,
    AlertCircle,
    AlertTriangle,
    Building as BuildingIcon,
    Calendar,
    ChevronDown,
    Clock,
    MapPin,
    MessageSquare,
    PenTool as Tool,
    Send,
    Users,
    X
} from 'lucide-react';
import {ComingSoon} from "../../components/ComingSoon/ComingSoon";
import LiveView360, {LiveElevatorViewData, StatusFilter} from './LiveView360';
import {CustomDateInput, CustomTimeInput} from '../../components/NewAlternativeView/CustomDateTimeInputs';
import {
    dateToString,
    stringToDate,
    stringToTime,
    timeToString,
    useDetectOutsideClick
} from '../../../utils/NewAlternativeView/utils';
import GeneralOperatingDataView from "./GeneralOperatingDataView";
import PerformanceDataView from "./PerformanceDataView";
import MaintenanceInformationView from "./MaintenanceInformationView";
import FaultInformationView from "./FaultInformationView";
import TrafficInformationView from "./TrafficInformationView";
import ElevatorIQAssistantView from "./ElevatorIQAssistantView";
import {DatePickerTimeRange} from "../../interfaces/NewAlternativeView/DatePickerTimeRange";
import {TimeRange} from "../../interfaces/NewAlternativeView/TimeRange";
import {DatePickerDateRange} from "../../interfaces/NewAlternativeView/DatePickerDateRange";
import {DateRange} from "../../interfaces/NewAlternativeView/DateRange";

class BuildingLocation {
    city: string;
    country?: string;

    constructor({city, country}: { city: string, country?: string }) {
        this.city = city;
        this.country = country;
    }

    toKey(): string {
        const cityPart = this.city.toLowerCase();
        const countryPart = this.country?.toLowerCase() || 'null'; // 'null' as string placeholder for key
        return `${cityPart}|${countryPart}`;
    }

    isEqual(other: BuildingLocation | null): boolean {
        if (!other) return false;
        return this.city === other.city && this.country === other.country;
    }

    toString(): string {
        if (this.country) {
            return `${this.city}, ${this.country}`;
        }
        return this.city;
    }
}


type TabType = 'liveview360' | 'operating' | 'performance' | 'maintenance' | 'fault' | 'traffic';

export type ElevatorTrafficRecord = {
    elevatorId: string;
    floor: number;
    peopleIn: number;
    peopleOut: number;
    peopleInElevator: number;
    dateTime: string;
};

// Types for react-datepicker state



function NewAlternateView() {
    // API Data State
    const [allBuildingsData, setAllBuildingsData] = useState<ApiBuildingInfo[]>([]);
    const [locations, setLocations] = useState<BuildingLocation[]>([]); // Stores unique city names
    const [buildingsInSelectedLocation, setBuildingsInSelectedLocation] = useState<ApiBuildingInfo[]>([]);
    const [isLoadingLocations, setIsLoadingLocations] = useState<boolean>(true);
    const [locationError, setLocationError] = useState<string | null>(null);

    // State for Maintenance Tab (New - using getMaintenanceLogs)
    const [maintenanceLogsData, setMaintenanceLogsData] = useState<ApiMaintenanceLogItem[]>([]);
    const [isLoadingMaintenanceLogs, setIsLoadingMaintenanceLogs] = useState<boolean>(false);
    const [maintenanceLogsError, setMaintenanceLogsError] = useState<string | null>(null);

    // State for Live 360 Camera View
    const [showCameraPanel, setShowCameraPanel] = useState(false);
    const [selectedCameraDevice, setSelectedCameraDevice] =
        useState<LiveElevatorViewData | null>(null);

    // State for Fault Logs
    const [faultLogsData, setFaultLogsData] = useState<ApiFaultLog[]>([]);
    const [isLoadingFaults, setIsLoadingFaults] = useState<boolean>(false);
    const [faultsError, setFaultsError] = useState<string | null>(null);

    // State for General Operating Data
    const [generalOperatingData, setGeneralOperatingData] = useState<GeneralOperatingData[]>([]);
    const [isLoadingGeneralOperatingData, setIsLoadingGeneralOperatingData] = useState<boolean>(false);
    const [generalOperatingDataError, setGeneralOperatingDataError] = useState<string | null>(null);

    // State for Performance Tab
    const [performanceData, setPerformanceData] = useState<PerformanceData | null>(null);
    const [isLoadingPerformance, setIsLoadingPerformance] = useState<boolean>(false);
    const [performanceError, setPerformanceError] = useState<string | null>(null);

    const [showAssistant, setShowAssistant] = useState(false);
    const [activeTab, setActiveTab] = useState<TabType>('liveview360');
    const [showLocationDropdown, setShowLocationDropdown] = useState(false);
    const [showBuildingDropdown, setShowBuildingDropdown] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [showTimePicker, setShowTimePicker] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState<BuildingLocation | null>(null);
    const [selectedBuilding, setSelectedBuilding] = useState<ApiBuildingInfo | null>(null);
    const [selectedBank, setSelectedBank] = useState<ApiBankInfo | null>(null);
    const [selectedDevice, setSelectedDevice] = useState<ApiDeviceInfo | null>(null);
    const [dateRange, setDateRange] = useState<DateRange>({
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });
    const [timeRange, setTimeRange] = useState<TimeRange>({
        startTime: '00:00',
        endTime: '23:59'
    });
    const [statusFilter, setStatusFilter] = useState<StatusFilter[]>(['all']);
    const [liveElevatorsForView, setLiveElevatorsForView] = useState<LiveElevatorViewData[]>([]);
    const [tempDateRange, setTempDateRange] = useState<DatePickerDateRange>({
        startDate: stringToDate(dateRange.startDate),
        endDate: stringToDate(dateRange.endDate)
    });
    const [tempTimeRange, setTempTimeRange] = useState<DatePickerTimeRange>({
        startTime: stringToTime(timeRange.startTime),
        endTime: stringToTime(timeRange.endTime)
    });

    const ELEVATOR_CAPACITY = 12;

    // Shared elevator traffic data — used by both Traffic and Fault tabs
    // Polls live data from the elevator stats API every second
    const [elevatorTrafficData, setElevatorTrafficData] = useState<ElevatorTrafficRecord[]>([]);
    const [isLoadingTraffic, setIsLoadingTraffic] = useState<boolean>(true);
    const [trafficError, setTrafficError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;

        const fetchTrafficData = async () => {
            try {
                const res = await fetch("http://localhost:8080/stats");
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json = await res.json();
                if (isMounted && json.Data && Array.isArray(json.Data)) {
                    setElevatorTrafficData((prev) => {
                        // Build a set of existing keys to avoid duplicates
                        const existingKeys = new Set(
                            prev.map((r) => `${r.elevatorId}_${r.dateTime}_${r.floor}`)
                        );
                        const newRecords = json.Data.filter(
                            (r: ElevatorTrafficRecord) =>
                                !existingKeys.has(`${r.elevatorId}_${r.dateTime}_${r.floor}`)
                        );
                        return newRecords.length > 0 ? [...prev, ...newRecords] : prev;
                    });
                    setTrafficError(null);
                    setIsLoadingTraffic(false);
                }
            } catch (err: any) {
                if (isMounted) {
                    setTrafficError(err.message || "Failed to fetch traffic data");
                    setIsLoadingTraffic(false);
                }
            }
        };

        fetchTrafficData(); // Initial fetch
        const interval = setInterval(fetchTrafficData, 1000);

        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, []);

    // Refs for dropdowns to detect outside clicks
    const locationDropdownRef = useRef<HTMLDivElement>(null);
    const buildingDropdownRef = useRef<HTMLDivElement>(null);
    const datePickerDropdownRef = useRef<HTMLDivElement>(null);
    const timePickerDropdownRef = useRef<HTMLDivElement>(null);

    // Setup click outside detection
    useDetectOutsideClick(locationDropdownRef, () => setShowLocationDropdown(false), showLocationDropdown);
    useDetectOutsideClick(buildingDropdownRef, () => setShowBuildingDropdown(false), showBuildingDropdown);
    useDetectOutsideClick(datePickerDropdownRef, () => setShowDatePicker(false), showDatePicker);
    useDetectOutsideClick(timePickerDropdownRef, () => setShowTimePicker(false), showTimePicker);

    // Effect for fetching initial building data
    // Effect to sync string date/time ranges to DatePicker's Date object states when they change
    useEffect(() => {
        setTempDateRange({startDate: stringToDate(dateRange.startDate), endDate: stringToDate(dateRange.endDate)});
    }, [dateRange]);

    useEffect(() => {
        setTempTimeRange({startTime: stringToTime(timeRange.startTime), endTime: stringToTime(timeRange.endTime)});
    }, [timeRange]);

    useEffect(() => {
        const fetchInitialData = async () => {
            setIsLoadingLocations(true);
            setLocationError(null);
            try {
                const data = await getBuildingInfos();
                setAllBuildingsData(data);

                let tempUniqueLocations: Record<string, BuildingLocation> = {};

                for (const building of data) {
                    // Assuming building.city is always present as per ApiBuildingInfo in solutrakApiService.ts
                    const loc = new BuildingLocation({
                        city: building.city, // city is non-optional in ApiBuildingInfo
                        country: building.country || undefined
                    });
                    const key = loc.toKey();

                    if (!tempUniqueLocations[key]) {
                        tempUniqueLocations[key] = loc;
                    }
                }

                const uniqueLocations = Object.values(tempUniqueLocations).sort(
                    (a, b) => a.toString().localeCompare(b.toString()) // Sort by the full display string
                );
                setLocations(uniqueLocations);

                if (uniqueLocations.length > 0) {
                    const firstLocation = uniqueLocations[0];
                    setSelectedLocation(firstLocation);
                    // Further filtering for buildings in this location and selecting the first building
                    // will be handled by the useEffect hook that depends on selectedLocation
                } else {
                    console.log('No locations found in building data.');
                    setSelectedLocation(null);
                    setBuildingsInSelectedLocation([]);
                    setSelectedBuilding(null);
                }
            } catch (error) {
                console.error("Error fetching building infos:", error);
                setLocationError(error instanceof Error ? error.message : 'Failed to load locations.');
                setAllBuildingsData([]);
                setLocations([]);
                setBuildingsInSelectedLocation([]);
                setSelectedLocation(null);
                setSelectedBuilding(null);
            }
            setIsLoadingLocations(false);
        };

        fetchInitialData();
    }, []);

    // Effect for updating buildings when selected location changes
    useEffect(() => {
        if (selectedLocation && allBuildingsData.length > 0) {
            const buildingsForCity = allBuildingsData.filter(b => {
                const buildingLoc = new BuildingLocation({
                    city: b.city, // city is non-optional in ApiBuildingInfo
                    country: b.country || undefined
                });
                return selectedLocation.isEqual(buildingLoc);
            });
            setBuildingsInSelectedLocation(buildingsForCity);
            if (buildingsForCity.length > 0) {
                setSelectedBuilding(buildingsForCity[0]);
            } else {
                setSelectedBuilding(null);
            }
        } else {
            setBuildingsInSelectedLocation([]);
            setSelectedBuilding(null);
        }
    }, [selectedLocation, allBuildingsData]);

    useEffect(() => {
        if (selectedBuilding?.bankInfos) {
            const transformedElevators: LiveElevatorViewData[] = [];
            selectedBuilding.bankInfos.forEach(bank => {
                bank.deviceInfos.forEach(device => {
                    let viewDirection: LiveElevatorViewData['direction'] = 'idle'; // Default to idle
                    const apiDirection = device.infoMessage?.direction;

                    if (apiDirection === "10") { // "10" typically signifies Up
                        viewDirection = 'up';
                    } else if (apiDirection === "20") { // "20" typically signifies Down
                        viewDirection = 'down';
                    }

                    transformedElevators.push({
                        id: device.deviceName ?? `Device ${device.deviceId}`,
                        floor: parseInt(device.infoMessage?.location ?? '0', 10),
                        doorStatus: parseInt(device.infoMessage.door),
                        direction: viewDirection,
                        status: device.status,
                        apiDeviceId: device.deviceId,
                        cameraUrl: device.cameraUrl
                    });
                });
            });
            setLiveElevatorsForView(transformedElevators);
        } else {
            setLiveElevatorsForView([]); // Clear if no building selected
        }
    }, [selectedBuilding, statusFilter]);

    // Effect to fetch Maintenance Logs (New - using getMaintenanceLogs)
    useEffect(() => {
        if (selectedDevice?.deviceId && dateRange.startDate && dateRange.endDate) {
            const fetchMaintenanceLogsData = async () => {
                setIsLoadingMaintenanceLogs(true); // Use new state setter
                setMaintenanceLogsError(null);    // Use new state setter
                try {
                    const logs = await getMaintenanceLogs({
                        deviceId: selectedDevice.deviceId,
                        beginningDate: dateRange.startDate,
                        endingDate: dateRange.endDate,
                    });
                    setMaintenanceLogsData(logs); // Use new state setter
                } catch (err) {
                    const errorMessage = err instanceof Error ? err.message : 'Failed to fetch maintenance logs';
                    setMaintenanceLogsError(errorMessage); // Use new state setter
                    console.error('Error fetching maintenance logs:', errorMessage);
                    setMaintenanceLogsData([]); // Clear data on error
                } finally {
                    setIsLoadingMaintenanceLogs(false); // Use new state setter
                }
            };
            fetchMaintenanceLogsData();
        } else {
            setMaintenanceLogsData([]); // Clear data if no device or date range selected
        }
    }, [selectedDevice, dateRange]); // Dependencies: selectedDevice and dateRange

    // Effect to fetch Fault Logs
    useEffect(() => {
        if (selectedDevice?.deviceId && dateRange.startDate && dateRange.endDate) {
            const fetchFaultLogsData = async () => {
                setIsLoadingFaults(true);
                setFaultsError(null);
                try {
                    const logs = await getFaultLogs({
                        deviceId: selectedDevice.deviceId,
                        beginningDate: dateRange.startDate,
                        endingDate: dateRange.endDate,
                    });
                    setFaultLogsData(logs);
                } catch (err) {
                    const errorMessage = err instanceof Error ? err.message : 'Failed to fetch fault logs';
                    setFaultsError(errorMessage);
                    console.error('Error fetching fault logs:', errorMessage);
                    setFaultLogsData([]); // Clear data on error
                } finally {
                    setIsLoadingFaults(false);
                }
            };
            fetchFaultLogsData();
        } else {
            setFaultLogsData([]); // Clear data if no device or date range selected
        }
    }, [selectedDevice, dateRange, setIsLoadingFaults, setFaultsError, setFaultLogsData]);

    // Effect to fetch General Operating Data
    useEffect(() => {
        if (selectedBank?.deviceInfos && dateRange.startDate && dateRange.endDate) {
            const fetchGeneralOperatingData = async () => {
                setIsLoadingGeneralOperatingData(true);
                setGeneralOperatingDataError(null);
                try {
                    const data = await getGeneralOperatingDataForDevices({
                        deviceIds: selectedBank.deviceInfos.map(device => device.deviceId),
                        beginningDate: dateRange.startDate,
                        endingDate: dateRange.endDate,
                    });
                    setGeneralOperatingData(data);
                } catch (err) {
                    const errorMessage = err instanceof Error ? err.message : 'Failed to fetch general operating data';
                    setGeneralOperatingDataError(errorMessage);
                    console.error('Error fetching general operating data:', errorMessage);
                    setGeneralOperatingData([]); // Clear data on error
                } finally {
                    setIsLoadingGeneralOperatingData(false);
                }
            };
            fetchGeneralOperatingData();
        } else {
            setGeneralOperatingData([]); // Clear data if no device or date range selected
        }
    }, [selectedBank, dateRange]); // Dependencies: selectedDevice and dateRange

    // Effect to fetch Performance Data
    useEffect(() => {
        const fetchPerformanceData = async (deviceId: number, beginningDate: string, endingDate: string) => {
            setIsLoadingPerformance(true);
            setPerformanceError(null);
            setPerformanceData(null); // Clear previous data
            try {
                const results = await Promise.allSettled([
                    getSolutrakUpDownTimeDataPoints(deviceId, beginningDate, endingDate),
                    getSolutrakDoorCycleCounts(deviceId, beginningDate, endingDate),
                    getSolutrakRunCounts(deviceId, beginningDate, endingDate),
                    getSolutrakLast24HoursCarCallCountDataPoints(deviceId, beginningDate, endingDate),
                    getSolutrakDoorCycleDataPoints(deviceId, beginningDate, endingDate),
                    getSolutrakLoadLevelDataPoints(deviceId, beginningDate, endingDate),
                    getSolutrakControllerTemperatureDataPoint(deviceId, beginningDate, endingDate),
                ]);

                const uptimeDataPoints = results[0]?.status === 'fulfilled' ? results[0].value : [];
                const doorCycleCounts = results[1]?.status === 'fulfilled' ? results[1].value : [];
                const runCounts = results[2]?.status === 'fulfilled' ? results[2].value : [];
                const hourlyCarCallCounts = results[3]?.status === 'fulfilled' ? results[3].value : [];
                const doorCyclesPerFloor = results[4]?.status === 'fulfilled' ? results[4].value : [];
                const loadLevels = results[5]?.status === 'fulfilled' ? results[5].value : [];
                const controllerTemperature = results[6]?.status === 'fulfilled' ? results[6].value : null;

                results.forEach((result, index) => {
                    if (result.status === 'rejected') {
                        console.error(`Error fetching performance data item ${index} (${['uptime', 'doorCycles', 'runCounts', 'hourlyCalls', 'doorCyclesPerFloor', 'loadLevels', 'controllerTemperatures'][index]}):`, result.reason);
                    }
                });

                // Check if all critical data fetching failed. Adjust this logic based on which data points are essential.
                const allEssentialFailed = results.slice(0, 3).every(result => result.status === 'rejected'); // Example: first 3 are essential
                if (allEssentialFailed && results.length > 0) {
                    throw new Error('Essential performance data API calls failed.');
                }

                setPerformanceData({
                    uptimeStats: uptimeDataPoints.length > 0 ? uptimeDataPoints : null,
                    doorCycleCounts,
                    runCounts,
                    hourlyCarCalls: hourlyCarCallCounts.length > 0 ? hourlyCarCallCounts : null,
                    doorCyclesPerFloor,
                    loadLevels,
                    controllerTemperature,
                    doorOpenTime: null,
                    doorCloseTime: null,
                    accelerationRate: null,
                    jerkRate: null,
                    maxSpeed: null,
                });

            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to fetch performance data';
                setPerformanceError(errorMessage);
                console.error('Error fetching performance data:', errorMessage);
                // setPerformanceData(null); // Already cleared at the beginning or handled by individual fallbacks
            } finally {
                setIsLoadingPerformance(false);
            }
        };

        if (selectedDevice?.deviceId && dateRange.startDate && dateRange.endDate) {
            fetchPerformanceData(selectedDevice.deviceId, dateRange.startDate, dateRange.endDate);
        } else {
            setPerformanceData(null); // Clear data if no device or date range
            setPerformanceError(null); // Clear any previous error
            setIsLoadingPerformance(false); // Ensure loading is false
        }
    }, [selectedDevice, dateRange.startDate, dateRange.endDate, setPerformanceData, setIsLoadingPerformance, setPerformanceError]);

    // Effect to reset bank and device selection when building changes
    useEffect(() => {
        setSelectedBank(null);
        setSelectedDevice(null);
        // Optionally, auto-select the first bank and device if a building is selected
        // if (selectedBuilding && selectedBuilding.bankInfos && selectedBuilding.bankInfos.length > 0) {
        //   const firstBank = selectedBuilding.bankInfos[0];
        //   setSelectedBank(firstBank);
        //   if (firstBank.deviceInfos && firstBank.deviceInfos.length > 0) {
        //     setSelectedDevice(firstBank.deviceInfos[0]);
        //   }
        // }
    }, [selectedBuilding]);

    const handleBankSelect = (bank: ApiBankInfo) => {
        setSelectedBank(bank);
        // If the current selected device is not in the newly selected bank, reset it.
        // Or, auto-select the first device of the new bank.
        if (!bank.deviceInfos.find(device => device.deviceId === selectedDevice?.deviceId)) {
            setSelectedDevice(null);
            // Optionally, select the first device of the new bank:
            // if (bank.deviceInfos && bank.deviceInfos.length > 0) {
            //   setSelectedDevice(bank.deviceInfos[0]);
            // }
        }
    };

    const handleDeviceSelect = (device: ApiDeviceInfo) => {
        setSelectedDevice(device);
    };


    const handleDateRangeApply = () => {
        setDateRange({
            startDate: dateToString(tempDateRange.startDate),
            endDate: dateToString(tempDateRange.endDate)
        });
        setShowDatePicker(false);
    };

    const handleTimeRangeApply = () => {
        setTimeRange({
            startTime: timeToString(tempTimeRange.startTime),
            endTime: timeToString(tempTimeRange.endTime)
        });
        setShowTimePicker(false);
    };

    const tabs = [
        {id: 'liveview360', label: 'LiveView 360', icon: Activity},
        {id: 'operating', label: 'General Operating Data', icon: Activity},
        {id: 'performance', label: 'Performance Data', icon: Activity},
        {id: 'maintenance', label: 'Maintenance Information', icon: Tool},
        {id: 'fault', label: 'Fault Information', icon: AlertCircle},
        {id: 'traffic', label: 'Traffic Information', icon: Users}
    ];

    const handleStatusFilterChange = (status: StatusFilter) => {
        if (status === 'all') {
            setStatusFilter(['all']);
        } else {
            const newFilters = statusFilter.includes('all')
                ? [status]
                : statusFilter.includes(status)
                    ? statusFilter.filter(s => s !== status)
                    : [...statusFilter, status];

            setStatusFilter(newFilters.length ? newFilters : ['all']);
        }
    };

    const filteredElevators = liveElevatorsForView.filter(elevator =>
        statusFilter.includes('all') || statusFilter.includes(elevator.status)
    );

    const handleLocationSelect = (location: BuildingLocation | null) => {
        setSelectedLocation(location);
        setSelectedBuilding(null);
        setShowLocationDropdown(false);
    };

    const handleBuildingSelect = (building: ApiBuildingInfo | null) => {
        setSelectedBuilding(building);
        setShowBuildingDropdown(false);
    };


    const renderTabContent = () => {
        switch (activeTab) {
            case 'liveview360':
                return (
                    <LiveView360
                    statusFilter={statusFilter}
                    handleStatusFilterChange={handleStatusFilterChange}
                    filteredElevators={filteredElevators}
                    setShowAssistant={setShowAssistant}
                    onShowCamera={(elevator) => {
                        setSelectedCameraDevice(elevator);
                        setShowCameraPanel(!showCameraPanel);
                    }}
                    />
                );
            case 'operating':
                return (
                    <GeneralOperatingDataView
                        selectedBuilding={selectedBuilding}
                        selectedBank={selectedBank}
                        isLoadingGeneralOperatingData={isLoadingGeneralOperatingData}
                        generalOperatingData={generalOperatingData}
                        generalOperatingDataError={generalOperatingDataError}
                        handleBankSelect={handleBankSelect}
                    />
                );
            case 'performance':
                return (
                    <PerformanceDataView
                        selectedBuilding={selectedBuilding}
                        selectedBank={selectedBank}
                        selectedDevice={selectedDevice}
                        isLoadingPerformance={isLoadingPerformance}
                        performanceData={performanceData}
                        performanceError={performanceError}
                        handleBankSelect={handleBankSelect}
                        handleDeviceSelect={handleDeviceSelect} />
                );

            case 'maintenance':
                return (
                    <MaintenanceInformationView />
                );
            case 'fault':
                return (
                    <FaultInformationView
                        selectedBuilding={selectedBuilding}
                        selectedBank={selectedBank}
                        selectedDevice={selectedDevice}
                        isLoadingFaults={isLoadingFaults}
                        faultLogsData={faultLogsData}
                        faultsError={faultsError}
                        handleBankSelect={handleBankSelect}
                        handleDeviceSelect={handleDeviceSelect}
                        elevatorTrafficData={elevatorTrafficData}
                        elevatorCapacity={ELEVATOR_CAPACITY} />
                );
            case 'traffic':
                return (<TrafficInformationView elevatorTrafficData={elevatorTrafficData} elevatorCapacity={ELEVATOR_CAPACITY} />);
            default:
                return null;
        }
    };

    return (
        <div className="ems-dashboard-container">
            <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col mx-auto w-full max-w-screen-2xl">
                {/* Header */}
                <div className="relative bg-slate-900/90 backdrop-blur-sm border-b border-slate-700/50 z-30">
                    <div className="w-full mx-auto px-6 py-4">
                        {/* Back Button and Building Title */}
                        <div className="flex items-center space-x-4 mb-6">
                            <div>
                                <h1 className="text-2xl font-semibold text-white">{selectedBuilding ? selectedBuilding.buildingName : (isLoadingLocations ? 'Loading...' : 'No Building Selected')}</h1>
                                <p className="text-sm text-slate-400">{selectedBuilding ? `${selectedBuilding.bankInfos.reduce((acc, bank) => acc + bank.deviceInfos.length, 0)} ELEVATORS` : (locationError ? 'Error loading' : '')}</p>
                            </div>
                        </div>

                        {/* Controls Row */}
                        <div className="flex items-center justify-between">
                            {/* Location and Building Selection */}
                            <div className="flex items-center space-x-4">
                                {/* Location Selector */}
                                <div className="relative">
                                    <button
                                        onClick={() => setShowLocationDropdown(!showLocationDropdown)}
                                        className="flex items-center space-x-2 bg-slate-800 px-4 py-2.5 rounded-lg hover:bg-slate-700 transition-colors text-white"
                                    >
                                        <MapPin size={18} className="text-slate-400"/>
                                        <span>{isLoadingLocations ? 'Loading...' : (selectedLocation && selectedLocation.toString()) || 'Select Location'}</span>
                                        <ChevronDown size={18} className="text-slate-400"/>
                                    </button>

                                    {showLocationDropdown && (
                                        // Attach ref to the location dropdown content container
                                        <div ref={locationDropdownRef}
                                             className="absolute top-full mt-2 w-64 bg-slate-800 rounded-lg shadow-lg shadow-black/20 border border-slate-700/50 backdrop-blur-sm z-50">
                                            {isLoadingLocations &&
                                                <div className="px-4 py-3 text-slate-400 text-left">Loading
                                                    locations...</div>}
                                            {locationError && <div
                                                className="px-4 py-3 text-red-500 text-left">Error: {locationError}</div>}
                                            {!isLoadingLocations && !locationError && locations.length === 0 &&
                                                <div className="px-4 py-3 text-slate-400 text-left">No locations
                                                    found.</div>}
                                            {!isLoadingLocations && !locationError && locations.map((location) => (
                                                <button
                                                    key={location.toString()}
                                                    onClick={() => handleLocationSelect(location)}
                                                    className="w-full px-4 py-3 text-left text-white hover:bg-slate-700 first:rounded-t-lg last:rounded-b-lg transition-colors"
                                                >
                                                    {location.toString()}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Building Selector */}
                                {selectedLocation && !isLoadingLocations && !locationError && (
                                    <div className="relative">
                                        <button
                                            onClick={() => setShowBuildingDropdown(!showBuildingDropdown)}
                                            disabled={buildingsInSelectedLocation.length === 0}
                                            className="flex items-center space-x-2 bg-slate-800 px-4 py-2.5 rounded-lg hover:bg-slate-700 transition-colors text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <BuildingIcon size={20} className="mr-2 text-slate-400"/>
                                            <span>{selectedBuilding ? selectedBuilding.buildingName : (buildingsInSelectedLocation.length === 0 && selectedLocation ? 'No buildings' : 'Select Building')}</span>
                                            <ChevronDown size={18} className="text-slate-400"/>
                                        </button>
                                        {showBuildingDropdown && (
                                            // Attach ref to the building dropdown content container
                                            <div ref={buildingDropdownRef}
                                                 className="absolute top-full mt-2 w-64 bg-slate-800 rounded-lg shadow-lg shadow-black/20 border border-slate-700/50 backdrop-blur-sm z-50">
                                                {buildingsInSelectedLocation.length > 0 ? buildingsInSelectedLocation.map(building => (
                                                    <button
                                                        key={building.buildingId}
                                                        onClick={() => handleBuildingSelect(building)}
                                                        className="w-full px-4 py-3 text-left text-white hover:bg-slate-700 first:rounded-t-lg last:rounded-b-lg transition-colors"
                                                    >
                                                        {building.buildingName}
                                                    </button>
                                                )) : <div className="px-4 py-3 text-slate-400 text-left">No buildings in
                                                    this location.</div>}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Date and Time Range Picker */}
                            <div className="flex items-center space-x-4">

                                {/* LLM Chat Button */}
                                <button
                                    onClick={() => {
                                        if (selectedBuilding) setShowAssistant(true);
                                    }}
                                    className="flex items-center space-x-2 bg-yellow-500 text-black px-4 py-2.5 rounded-lg hover:bg-yellow-400 transition-colors"
                                >
                                    <MessageSquare size={18} className="text-black"/>
                                    <span>LLM Chat</span>
                                </button>


                                {/* Date Range Picker */}
                                <div className="relative">
                                    <button
                                        onClick={() => setShowDatePicker(!showDatePicker)}
                                        className="flex items-center space-x-2 bg-slate-800 px-4 py-2.5 rounded-lg hover:bg-slate-700 transition-colors text-white"
                                    >
                                        <Calendar size={18} className="text-slate-400"/>
                                        <span>{dateRange.startDate} - {dateRange.endDate}</span>
                                    </button>

                                    {showDatePicker && (
                                        // Attach ref to the date picker dropdown content container
                                        <div ref={datePickerDropdownRef}
                                             className="absolute top-full right-0 mt-2 bg-slate-800 rounded-lg shadow-lg shadow-black/20 border border-slate-700/50 backdrop-blur-sm p-4 z-[100] w-80">
                                            <div className="space-y-4">
                                                <div className="space-y-2">
                                                    <label className="block text-sm font-medium text-slate-400">Start
                                                        Date</label>
                                                    <DatePicker
                                                        selected={tempDateRange.startDate}
                                                        onChange={(date: Date | null) => setTempDateRange(prev => ({
                                                            ...prev,
                                                            startDate: date
                                                        }))}
                                                        selectsStart
                                                        startDate={tempDateRange.startDate}
                                                        endDate={tempDateRange.endDate}
                                                        maxDate={tempDateRange.endDate}
                                                        customInput={<CustomDateInput/>}
                                                        dateFormat="yyyy-MM-dd"
                                                        wrapperClassName="w-full"
                                                        popperPlacement="bottom-start"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="block text-sm font-medium text-slate-400">End
                                                        Date</label>
                                                    <DatePicker
                                                        selected={tempDateRange.endDate}
                                                        onChange={(date: Date | null) => setTempDateRange(prev => ({
                                                            ...prev,
                                                            endDate: date
                                                        }))}
                                                        selectsEnd
                                                        startDate={tempDateRange.startDate}
                                                        endDate={tempDateRange.endDate}
                                                        minDate={tempDateRange.startDate}
                                                        customInput={<CustomDateInput/>}
                                                        dateFormat="yyyy-MM-dd"
                                                        wrapperClassName="w-full"
                                                        popperPlacement="bottom-start"
                                                    />
                                                </div>
                                                <button
                                                    onClick={handleDateRangeApply}
                                                    className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition-colors"
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Time Range Picker */}
                                <div className="relative">
                                    <button
                                        onClick={() => setShowTimePicker(!showTimePicker)}
                                        className="flex items-center space-x-2 bg-slate-800 px-4 py-2.5 rounded-lg hover:bg-slate-700 transition-colors text-white"
                                    >
                                        <Clock size={18} className="text-slate-400"/>
                                        <span>{timeRange.startTime} - {timeRange.endTime}</span>
                                    </button>

                                    {showTimePicker && (
                                        // Attach ref to the time picker dropdown content container
                                        <div ref={timePickerDropdownRef}
                                             className="absolute top-full right-0 mt-2 bg-slate-800 rounded-lg shadow-lg shadow-black/20 border border-slate-700/50 backdrop-blur-sm p-4 z-[100] w-80">
                                            <div className="space-y-4">
                                                <div className="space-y-2">
                                                    <label className="block text-sm font-medium text-slate-400">Start
                                                        Time</label>
                                                    <DatePicker
                                                        selected={tempTimeRange.startTime}
                                                        onChange={(date: Date | null) => setTempTimeRange(prev => ({
                                                            ...prev,
                                                            startTime: date
                                                        }))}
                                                        customInput={<CustomTimeInput/>}
                                                        showTimeSelect
                                                        showTimeSelectOnly
                                                        timeIntervals={15}
                                                        timeCaption="Start Time"
                                                        dateFormat="HH:mm"
                                                        wrapperClassName="w-full"
                                                        popperPlacement="bottom-start"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="block text-sm font-medium text-slate-400">End
                                                        Time</label>
                                                    <DatePicker
                                                        selected={tempTimeRange.endTime}
                                                        onChange={(date: Date | null) => {
                                                            if (tempTimeRange.startTime && date &&
                                                                tempDateRange.startDate?.toDateString() === tempDateRange.endDate?.toDateString() &&
                                                                date < tempTimeRange.startTime) {
                                                                setTempTimeRange(prev => ({
                                                                    ...prev,
                                                                    endTime: prev.startTime
                                                                }));
                                                            } else {
                                                                setTempTimeRange(prev => ({...prev, endTime: date}));
                                                            }
                                                        }}
                                                        customInput={<CustomTimeInput/>}
                                                        showTimeSelect
                                                        showTimeSelectOnly
                                                        timeIntervals={15}
                                                        timeCaption="End Time"
                                                        dateFormat="HH:mm"
                                                        wrapperClassName="w-full"
                                                        popperPlacement="bottom-start"
                                                        minTime={tempTimeRange.startTime && tempDateRange.startDate?.toDateString() === tempDateRange.endDate?.toDateString() ? tempTimeRange.startTime : undefined}
                                                        maxTime={tempTimeRange.startTime && tempDateRange.startDate?.toDateString() === tempDateRange.endDate?.toDateString() ? (() => {
                                                            const d = new Date(tempTimeRange.startTime!);
                                                            d.setHours(23, 59, 59, 999);
                                                            return d;
                                                        })() : undefined}
                                                    />
                                                </div>
                                                <button
                                                    onClick={handleTimeRangeApply}
                                                    className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition-colors"
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="w-full mx-auto p-6">
                    <div className="space-y-6">
                        {/* Navigation Tabs */}
                        <div className="bg-slate-800/90 backdrop-blur-sm rounded-xl border border-slate-700/50 p-1.5">
                            <div className="flex justify-between">
                                {tabs.map((tab) => {
                                    const Icon = tab.icon;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id as TabType)}
                                            className={`flex-1 flex items-center justify-center px-6 py-3.5 text-sm font-medium rounded-lg transition-all ${
                                                activeTab === tab.id
                                                    ? 'bg-slate-700 text-white shadow-sm ring-1 ring-slate-600'
                                                    : 'text-slate-400 hover:text-slate-300 hover:bg-slate-700/50'
                                            }`}
                                        >
                                            <Icon size={16} className="mr-2"/>
                                            {tab.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Tab Content */}
                        {renderTabContent()}
                    </div>
                </div>

                {/* ElevatorIQ Assistant */}
                {showAssistant &&
                    (
                        <ElevatorIQAssistantView
                            setShowAssistant={setShowAssistant}
                            selectedBuilding={selectedBuilding}
                            selectedBank={selectedBank}
                            selectedDevice={selectedDevice}/>
                    )
                }

                {/* Camera Panel */}
                {showCameraPanel && (
                    <CameraSidePanelView
                        elevator={selectedCameraDevice}
                        onClose={() => setShowCameraPanel(false)}
                    />
                )}
            </div>
        </div>
    );
}

export default NewAlternateView;
